const ELLIPSIS = '…';
const TRAILING_PUNCTUATION = /[\s.,;:!?-]+$/u;
const SHRINK_ITERATIONS_LIMIT = 500;

interface ClampPoint {
  node: Text;
  offset: number;
}

export function getLineHeight(element: HTMLElement): number {
  const { lineHeight, fontSize } = window.getComputedStyle(element);
  const parsedLineHeight = parseFloat(lineHeight);

  if (!Number.isNaN(parsedLineHeight)) {
    return parsedLineHeight;
  }

  return parseFloat(fontSize) * 1.2;
}

export function clampToLines(element: HTMLElement, lines: number): void {
  const maxHeight = getLineHeight(element) * lines;

  element.style.maxHeight = `${maxHeight}px`;
  element.style.overflow = 'hidden';

  if (element.scrollHeight <= maxHeight + 1) {
    return;
  }

  const limitBottom = element.getBoundingClientRect().top + maxHeight;
  const point = findClampPoint(element, limitBottom);

  if (!point || !element.lastChild) {
    return;
  }

  const tail = document.createRange();

  tail.setStart(point.node, point.offset);
  tail.setEndAfter(element.lastChild);
  tail.deleteContents();

  setTextWithEllipsis(point.node, point.node.textContent ?? '');
  shrinkUntilFits(element, point.node, maxHeight);
}

function findClampPoint(element: HTMLElement, limitBottom: number): Nullable<ClampPoint> {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let point: Nullable<ClampPoint> = null;
  let node = walker.nextNode() as Nullable<Text>;

  while (node) {
    range.selectNodeContents(node);

    const { top, bottom, height } = range.getBoundingClientRect();

    if (height > 0) {
      if (bottom <= limitBottom) {
        point = { node, offset: node.length };
      } else {
        if (top < limitBottom) {
          const offset = findLastVisibleOffset(node, limitBottom);

          if (offset > 0) {
            point = { node, offset };
          }
        }

        return point;
      }
    }

    node = walker.nextNode() as Nullable<Text>;
  }

  return point;
}

function findLastVisibleOffset(node: Text, limitBottom: number): number {
  const range = document.createRange();
  let low = 0;
  let high = node.length;

  while (low < high) {
    const middle = Math.ceil((low + high) / 2);

    range.setStart(node, 0);
    range.setEnd(node, middle);

    if (range.getBoundingClientRect().bottom <= limitBottom) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }

  return low;
}

function setTextWithEllipsis(node: Text, text: string): void {
  node.textContent = `${text.replace(TRAILING_PUNCTUATION, '')}${ELLIPSIS}`;
}

function shrinkUntilFits(element: HTMLElement, node: Text, maxHeight: number): void {
  for (let iteration = 0; iteration < SHRINK_ITERATIONS_LIMIT; iteration++) {
    if (element.scrollHeight <= maxHeight + 1) {
      return;
    }

    const text = node.textContent ?? '';

    if (text.length <= ELLIPSIS.length) {
      return;
    }

    setTextWithEllipsis(node, text.slice(0, -1 - ELLIPSIS.length));
  }
}
